import React, { useState, useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import ExpensesPage from "./pages/ExpensesPage";

import IncomePage from "./pages/IncomePage";
import DashboardPage from "./pages/DashboardPage";
import DebtsPage from "./pages/DebtsPage";
import AccountsPage from "./pages/AccountsPage";
import TransactionsPage from "./pages/TransactionsPageNew";
import TrendsPage from "./pages/TrendsPage";
import SettingsPage from "./pages/SettingsPage";
import ToolsPage from "./pages/ToolsPage";
import GoalsPage from "./pages/GoalsPage";
import GroupsPage from "./pages/GroupsPage";
import GroupCreatePage from "./pages/GroupCreatePage";
import GroupDetailPage from "./pages/GroupDetailPage";
import GroupMembersPage from "./pages/GroupMembersPage";
import GroupSharedPage from "./pages/GroupSharedPage";
import GroupDeletedRecordsPage from "./pages/GroupDeletedRecordsPage";
import GroupInvitePage from "./pages/groups/GroupInvitePage";
import SharedDebtsPage from "./pages/SharedDebtsPage";
import SharedDashboardPage from "./pages/SharedDashboardPage";
import SharedExpensesPage from "./pages/SharedExpensesPage";
import SharedIncomePage from "./pages/SharedIncomePage";
import SharedShoppingPage from "./pages/SharedShoppingPage";
import SharedTaxCalculatorToolPage from "./pages/SharedTaxCalculatorToolPage";
import SharedHousingForecastToolPage from "./pages/SharedHousingForecastToolPage";
import SharedMortgageCalculatorToolPage from "./pages/SharedMortgageCalculatorToolPage";
import SharedWifeValueCalculatorToolPage from "./pages/SharedWifeValueCalculatorToolPage";
import NotFoundPage from "./pages/NotFoundPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import DocumentationPage from "./pages/DocumentationPage";
import PrimeAgentSalesPage from "./pages/PrimeAgentSalesPage";
import ApiDocsPage from "./pages/ApiDocsPage";
import AppLayout from "./components/layouts/AppLayout";
import { isMigrationComplete } from "./services/database";
import { logLocalStorage } from "./lib";
import { AppThemeProvider } from "./contexts/AppThemeProvider";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import OnboardingCheck from "./components/auth/OnboardingCheck";
import { setAppTimezone } from "./lib/dateUtils";
import { settingsService } from "./services/settings/settingsService";

function App() {
  const [showMigration, setShowMigration] = useState(true);
  const [apiConnected, setApiConnected] = useState(true);
  const location = useLocation();

  // Initialize timezone from user settings
  useEffect(() => {
    settingsService
      .getUserSettings()
      .then((settings) => {
        if (settings?.timezone) {
          setAppTimezone(settings.timezone);
          console.log("[App] Timezone initialized:", settings.timezone);
        }
      })
      .catch(() => {
        console.log("[App] Using default timezone: America/Los_Angeles");
      });
  }, []);

  // Check API connection status
  useEffect(() => {
    const checkApiConnection = async () => {
      try {
        const response = await fetch("/api/health", {
          credentials: "include",
          cache: "no-store",
        });
        setApiConnected(response.ok);
      } catch (error) {
        console.warn("[App] API health check failed, retrying", error);
        try {
          const response = await fetch("/api/health", {
            mode: "no-cors",
          });
          setApiConnected(response.type === "opaque");
        } catch {
          setApiConnected(false);
        }
      }
    };

    checkApiConnection();
    const interval = setInterval(checkApiConnection, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);

  // Check if we should show migration prompt
  useEffect(() => {
    if (isMigrationComplete()) {
      setShowMigration(false);
    }

    // Debug: Log localStorage contents
    logLocalStorage();

    // Debug: Force migration if needed
    // Temporarily disabled to prevent infinite reload loop
    // if (!isMigrationComplete()) {
    //   forceMigration().then(() => {
    //     setShowMigration(false);
    //     window.location.reload(); // Reload to apply changes
    //   });
    // }
  }, []);

  // Determine current page from URL path
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path.includes("/expenses")) return "expenses";
    if (path.includes("/income")) return "income";
    if (path.includes("/debts")) return "debts";
    if (path.includes("/goals")) return "goals";
    if (path.includes("/accounts")) return "accounts";
    if (path.includes("/transactions")) return "transactions";
    if (path.includes("/trends")) return "trends";
    if (path.includes("/groups")) return "groups";
    if (path.includes("/tools")) return "tools";
    if (path.includes("/api-docs")) return "tools";
    if (path.includes("/settings")) return "settings";
    return "dashboard";
  };

  // Don't redirect from root anymore - show landing page
  // useEffect(() => {
  //   if (location.pathname === "/") {
  //     navigate("/dashboard");
  //   }
  // }, [location.pathname, navigate]);

  // Handle API toggle (placeholder for now)
  const handleToggleApi = () => {
    alert(
      `API Server is currently ${
        apiConnected ? "ON" : "OFF"
      }.\n\nTo toggle the server, use the terminal:\n- Start: cd /Users/spencerhill/Sites/finance && node server/api-server.js\n- Stop: Kill the process with Ctrl+C`
    );
  };

  // Handle migration completion
  const handleMigrationComplete = () => {
    setShowMigration(false);
  };

  return (
    <AppThemeProvider>
      <AuthProvider>
        <div style={{ minHeight: "100vh" }}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/docs" element={<DocumentationPage />} />
            <Route path="/api-docs" element={<ApiDocsPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/prime-agent" element={<PrimeAgentSalesPage />} />
            <Route path="/groups/invite/:token" element={<GroupInvitePage />} />
            <Route
              path="/shared/debts/:shareId"
              element={<SharedDebtsPage />}
            />
            <Route
              path="/shared/expenses/:shareId"
              element={<SharedExpensesPage />}
            />
            <Route
              path="/shared/income/:shareId"
              element={<SharedIncomePage />}
            />
            <Route
              path="/shared/shopping/:shareId"
              element={<SharedShoppingPage />}
            />
            <Route
              path="/shared/tax-calculator"
              element={<SharedTaxCalculatorToolPage />}
            />
            <Route
              path="/shared/housing-forecast"
              element={<SharedHousingForecastToolPage />}
            />
            <Route
              path="/shared/mortgage-calculator"
              element={<SharedMortgageCalculatorToolPage />}
            />
            <Route
              path="/shared/wife-value-calculator"
              element={<SharedWifeValueCalculatorToolPage />}
            />
            <Route
              path="/shared/spouse-value-calculator"
              element={<SharedWifeValueCalculatorToolPage />}
            />
            <Route
              path="/shared/dashboards/:shareId"
              element={<SharedDashboardPage />}
            />
            <Route path="*" element={<NotFoundPage />} />

            {/* Protected App Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <DashboardPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <ExpensesPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            {/* Redirect old comparison URL to new tools location */}
            <Route
              path="/expenses/comparison"
              element={<Navigate to="/tools/expenses-comparison" replace />}
            />
            <Route
              path="/income"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <IncomePage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/debts"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <DebtsPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/goals"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <GoalsPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <AccountsPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <TransactionsPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />

            <Route
              path="/trends"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <TrendsPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <SettingsPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/groups"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <GroupsPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/groups/new"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <GroupCreatePage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/groups/:id"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <GroupDetailPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/groups/:id/members"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <GroupMembersPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/groups/:id/shared"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <GroupSharedPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/groups/:id/deleted"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <GroupDeletedRecordsPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <ToolsPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/forecasting"
              element={<Navigate to="/tools/debt-payoff-forecasting" replace />}
            />
            <Route
              path="/tools/:toolId"
              element={
                <ProtectedRoute>
                  <OnboardingCheck>
                    <AppLayout
                      currentPage={getCurrentPage()}
                      apiConnected={apiConnected}
                      onToggleApi={handleToggleApi}
                      showMigration={showMigration}
                      onMigrationComplete={handleMigrationComplete}
                    >
                      <ToolsPage />
                    </AppLayout>
                  </OnboardingCheck>
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </AuthProvider>
    </AppThemeProvider>
  );
}

export default App;
